import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Data } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { DialogConfirmacaoComponent } from '@components/dialog-confirmacao/dialog-confirmacao.component';

import { ConfigurarRelatorioCaixaApiService } from '../services/configurar-relatorio-caixa-api.service';
import { ConfigurarRelatorioCaixaResolverData } from '../models/configurar-relatorio-caixa-resolver-data';
import { RelatorioCaixaListItem } from '../models/relatorio-caixa-list-item';
import { RelatorioCaixa } from '../models/relatorio-caixa';
import { CategoriaRelatorio, TipoLancamentoCategoria } from '../models/categoria-relatorio';
import { TipoLancamento } from '@models/tipo-lancamento';
import { MessageService } from '@services/message.service';

export interface ContaComTipos {
  nomeConta: string;
  tipos: TipoLancamento[];
}

@Component({
  selector: 'app-configurar-relatorio-caixa',
  standalone: false,
  templateUrl: './configurar-relatorio-caixa.component.html',
  styleUrl: './configurar-relatorio-caixa.component.scss'
})
export class ConfigurarRelatorioCaixaComponent implements OnInit {

  relatorios: RelatorioCaixaListItem[] = [];
  contasComTipos: ContaComTipos[] = [];
  expandedRows: any = {};

  relatorio: RelatorioCaixa = this.novoRelatorioVazio();
  categoriaAtiva: CategoriaRelatorio;
  tiposEmOutrasCategorias: Map<number, CategoriaRelatorio> = new Map();
  salvando = false;
  buscaGeral: string = '';

  constructor(
    private route: ActivatedRoute,
    private configurarRelatorioCaixaApiService: ConfigurarRelatorioCaixaApiService,
    private messageService: MessageService,
    private dialog: MatDialog,
  ) { }

  ngOnInit(): void {
    this.route.data.subscribe((data: Data) => {
      const resolverData = data['resolverData'] as ConfigurarRelatorioCaixaResolverData;
      this.relatorios = resolverData.relatorios;
      this.contasComTipos = this.agruparTiposPorConta(resolverData.tiposLancamento);
      this.iniciarNovoRelatorio();
    });
  }


  public onRelatorioSelecionado(item: RelatorioCaixaListItem | null): void {
    if (!item) return;
    this.configurarRelatorioCaixaApiService.getById(item.id).subscribe((relatorio: RelatorioCaixa) => {
      this.relatorio = relatorio;

      if (this.relatorio.categorias.length === 0) {
        const primeiraCategoria: CategoriaRelatorio = {
          id: 0, nome: 'Categoria 1', identificadorCategoria: null, observacaoCategoria: null, tiposLancamento: [],
        };
        this.relatorio.categorias.push(primeiraCategoria);
      }

      this.categoriaAtiva = this.relatorio.categorias[0];
      this.atualizarTiposEmOutrasCategorias();
    });
  }

  public novoClick(): void {
    this.iniciarNovoRelatorio();
  }

  get categoriasFiltradas(): CategoriaRelatorio[] {
    const termo = this.buscaGeral?.trim().toLowerCase();
    if (!termo) return this.relatorio.categorias;
    return this.relatorio.categorias.filter(c => c.nome?.toLowerCase().includes(termo));
  }

  get contasFiltradas(): ContaComTipos[] {
    const termo = this.buscaGeral?.trim().toLowerCase();
    if (!termo) return this.contasComTipos;
    return this.contasComTipos.filter(conta =>
      conta.nomeConta.toLowerCase().includes(termo) ||
      conta.tipos.some(t => t.nome.toLowerCase().includes(termo))
    );
  }

  public selecionarCategoria(categoria: CategoriaRelatorio): void {
    this.categoriaAtiva = categoria;
    this.atualizarTiposEmOutrasCategorias();
  }

  public adicionarCategoria(): void {
    const erroNome = this.validarNomesCategorias();
    if (erroNome) {
      this.messageService.showToastWarning(erroNome);
      return;
    }

    const novaCategoria: CategoriaRelatorio = {
      id: 0,
      nome: `Categoria ${this.relatorio.categorias.length + 1}`,
      identificadorCategoria: null,
      observacaoCategoria: null,
      tiposLancamento: [],
    };
    this.relatorio.categorias.push(novaCategoria);
    this.categoriaAtiva = novaCategoria;
    this.atualizarTiposEmOutrasCategorias();
  }

  public removerCategoriaClick(categoria: CategoriaRelatorio): void {
    const dialogRef = this.dialog.open(DialogConfirmacaoComponent, {
      width: '467px',
      panelClass: 'no-padding-dialog',
      disableClose: true,
    });
    dialogRef.componentInstance.titulo = 'Remover categoria';
    dialogRef.componentInstance.mensagem = `A categoria "${categoria.nome}" será removida do relatório.`;
    dialogRef.componentInstance.textoBtnConfirmacao = 'Remover';
    dialogRef.componentInstance.btnBackgroundColor = '#F44336';
    dialogRef.componentInstance.acaoConfirmacao = () => this.removerCategoria(categoria);
  }

  private removerCategoria(categoria: CategoriaRelatorio): void {
    const index = this.relatorio.categorias.indexOf(categoria);
    this.relatorio.categorias.splice(index, 1);
    if (this.categoriaAtiva === categoria)
      this.categoriaAtiva = this.relatorio.categorias[0] ?? null;
    this.atualizarTiposEmOutrasCategorias();
  }

  // Checkbox conta
  public isContaDisabled(conta: ContaComTipos): boolean {
    return !this.categoriaAtiva || conta.tipos.every(t => this.tiposEmOutrasCategorias.has(t.id));
  }

  public isContaSelecionada(conta: ContaComTipos): boolean {
    return conta.tipos.every(t => this.categoriaAtiva?.tiposLancamento.some(tl => tl.id === t.id));
  }

  public isContaIndeterminate(conta: ContaComTipos): boolean {
    const algum = conta.tipos.some(t => this.categoriaAtiva?.tiposLancamento.some(tl => tl.id === t.id));
    return algum && !this.isContaSelecionada(conta);
  }

  public toggleConta(conta: ContaComTipos): void {
    if (this.isContaDisabled(conta)) return;
    if (this.isContaSelecionada(conta)) {
      conta.tipos.forEach(t => this.removerTipo(t));
    } else {
      conta.tipos
        .filter(t => !this.tiposEmOutrasCategorias.has(t.id))
        .forEach(t => this.adicionarTipo(t));
    }
  }

  // Checkbox tipo
  public isTipoSelecionado(tipoId: number): boolean {
    return this.categoriaAtiva?.tiposLancamento.some(t => t.id === tipoId) ?? false;
  }

  public isTipoDisabled(tipoId: number): boolean {
    return !this.categoriaAtiva || this.tiposEmOutrasCategorias.has(tipoId);
  }

  public categoriaDoTipo(tipoId: number): CategoriaRelatorio | undefined {
    return this.tiposEmOutrasCategorias.get(tipoId);
  }

  public toggleTipo(tipo: TipoLancamentoCategoria): void {
    if (this.isTipoDisabled(tipo.id)) return;
    if (this.isTipoSelecionado(tipo.id)) {
      this.removerTipo(tipo);
    } else {
      this.adicionarTipo(tipo);
    }
  }

  public removerTipoDeOutraCategoriaClick(tipo: TipoLancamentoCategoria, event: Event): void {
    event.stopPropagation();
    const categoria = this.tiposEmOutrasCategorias.get(tipo.id);
    if (categoria) this.removerTipoDeCategoria(tipo, categoria);
  }

  public salvar(): void {
    const erroNome = this.validarNomesCategorias();
    if (erroNome) { this.messageService.showToastWarning(erroNome); return; }

    const erroTipo = this.validarTiposDuplicados();
    if (erroTipo) { this.messageService.showToastWarning(erroTipo); return; }

    if (!this.relatorio.nome?.trim()) {
      this.messageService.showToastWarning('O nome do relatório não pode ser vazio');
      return;
    }

    this.salvando = true;

    if (this.relatorio.id === 0) {
      this.configurarRelatorioCaixaApiService.criar(this.relatorio).subscribe({
        next: (id: number) => {
          this.relatorio.id = id;
          this.messageService.showToastSuccess('Relatório criado com sucesso');
          this.recarregarLista();
          this.salvando = false;
        },
        error: () => { this.salvando = false; }
      });
    } else {
      this.configurarRelatorioCaixaApiService.editar(this.relatorio.id, this.relatorio).subscribe({
        next: () => {
          this.messageService.showToastSuccess('Relatório salvo com sucesso');
          this.recarregarLista();
          this.salvando = false;
        },
        error: () => { this.salvando = false; }
      });
    }
  }

  private adicionarTipo(tipo: TipoLancamentoCategoria): void {
    if (this.categoriaAtiva.tiposLancamento.some(t => t.id === tipo.id)) return;
    if (this.tiposEmOutrasCategorias.has(tipo.id)) return;
    this.categoriaAtiva.tiposLancamento.push(tipo);
    this.atualizarTiposEmOutrasCategorias();
  }

  private removerTipo(tipo: TipoLancamentoCategoria): void {
    this.removerTipoDeCategoria(tipo, this.categoriaAtiva);
  }

  private removerTipoDeCategoria(tipo: TipoLancamentoCategoria, categoria: CategoriaRelatorio): void {
    const idx = categoria.tiposLancamento.findIndex(t => t.id === tipo.id);
    if (idx >= 0) {
      categoria.tiposLancamento.splice(idx, 1);
      this.atualizarTiposEmOutrasCategorias();
    }
  }

  private atualizarTiposEmOutrasCategorias(): void {
    this.tiposEmOutrasCategorias = new Map(
      this.relatorio.categorias
        .filter(c => c !== this.categoriaAtiva)
        .flatMap(c => c.tiposLancamento.map(t => [t.id, c] as [number, CategoriaRelatorio]))
    );
  }

  private validarNomesCategorias(): string | null {
    const nomes = this.relatorio.categorias.map(c => c.nome?.trim().toLowerCase());
    const duplicado = nomes.find((n, i) => nomes.indexOf(n) !== i);
    if (duplicado) return `Existe mais de uma categoria com o nome "${duplicado}"`;

    const vazio = this.relatorio.categorias.find(c => !c.nome?.trim());
    if (vazio) return 'Todas as categorias precisam ter um nome';

    return null;
  }

  private validarTiposDuplicados(): string | null {
    const vistos = new Set<number>();
    for (const categoria of this.relatorio.categorias) {
      for (const tipo of categoria.tiposLancamento) {
        if (vistos.has(tipo.id))
          return `O tipo "${tipo.nome}" aparece em mais de uma categoria`;
        vistos.add(tipo.id);
      }
    }
    return null;
  }

  private iniciarNovoRelatorio(): void {
    const primeiraCategoria: CategoriaRelatorio = {
      id: 0, nome: 'Categoria 1', identificadorCategoria: null, observacaoCategoria: null, tiposLancamento: [],
    };
    this.relatorio = { id: 0, nome: '', categorias: [primeiraCategoria] };
    this.categoriaAtiva = primeiraCategoria;
    this.atualizarTiposEmOutrasCategorias();
  }

  private novoRelatorioVazio(): RelatorioCaixa {
    return { id: 0, nome: '', categorias: [] };
  }

  private agruparTiposPorConta(tipos: TipoLancamento[]): ContaComTipos[] {
    const map = new Map<string, TipoLancamento[]>();
    tipos.forEach(t => {
      if (!map.has(t.nomeConta)) map.set(t.nomeConta, []);
      map.get(t.nomeConta)!.push(t);
    });
    return Array.from(map.entries()).map(([nomeConta, tipos]) => ({ nomeConta, tipos }));
  }

  private recarregarLista(): void {
    this.configurarRelatorioCaixaApiService.listar().subscribe((relatorios: RelatorioCaixaListItem[]) => {
      this.relatorios = relatorios;
    });
  }
}
